import { Controller } from "react-hook-form";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { states } from "../utils/states";
import RegisterCustomerInvoice from "./regCustomerInv";
import { Button } from "../ui/button";
import {
  useSelectedState,
  useTempSelectedBuildingName,
} from "./buildingStates";
import { useDialog } from "../utils/isDialogOpen";
import { fetchBuildings } from "./getBuilding";
import { useEffect } from "react";
import { getBuildings } from "@/api/buildings/get-Buildings";
import { editBuilding } from "./editBuilding";
import { useEditState } from "../expenses/expenseStates";

export default function SelectAndEditBuildingInvoice() {
  const { tempSelectedBuildingName, setTempSelectedBuildingName } =
    useTempSelectedBuildingName();
  const { setIsDialogOpen } = useDialog();
  const { buildings } = fetchBuildings();
  const { selectedBuilding, setSelectedBuilding } = useSelectedState();
  const { setIsEditOpen } = useEditState();

  useEffect(() => {
    const fetchBuildingDetails = async () => {
      if (tempSelectedBuildingName) {
        const buildingResponse = await getBuildings(
          1,
          1,
          tempSelectedBuildingName,
        );
        const building = buildingResponse.buildings[0];
        setSelectedBuilding({
          id: building.id,
          buildingName: building.buildingName,
          address: building.address,
          buildingCity: building.buildingCity,
          buildingState: building.buildingState,
          buildingZip: building.buildingZip,
          buildingCountry: building.buildingCountry,
          letterIdBuilding: building.letterIdBuilding,
        });
      }
    };
    fetchBuildingDetails();
  }, [tempSelectedBuildingName]);

  const {
    handleSubmit: handleSubmitEditBuilding,
    register: registerEditBuilding,
    updateBuildingFn,
    errors: errorsEditBuilding,
    isSubmitting: isSubmittingEditBuilding,
    control: controlEditBuilding,
  } = editBuilding();

  return (
    <div>
      <div className="flex items-center space-x-2">
        <label className="font-Hemmers text-black">Bill to:</label>
        <Select
          onValueChange={(value) => {
            if (value === "addNewBuilding") {
              setIsDialogOpen(true);
            } else {
              setTempSelectedBuildingName(value || undefined);
            }
          }}
          value={tempSelectedBuildingName ?? ""} // to display the value after selection
        >
          <SelectTrigger className="w-36 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm">
            <SelectValue placeholder="Customer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="addNewBuilding">
              <span className="flex text-blue-600">Add New</span>
            </SelectItem>
            {buildings?.buildings.map((building) => (
              <SelectItem
                key={building.buildingName}
                value={building.buildingName}
              >
                {building.buildingName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <RegisterCustomerInvoice></RegisterCustomerInvoice>
      </div>

      {/* Display selected building information */}
      {!selectedBuilding ? (
        <>
          <Skeleton className="mt-1 h-4 w-[250px] bg-[#eeeff0]" />
          <Skeleton className="mt-1 h-4 w-[225px] bg-[#eeeff0]" />
          <Skeleton className="mt-1 h-4 w-[200px] bg-[#eeeff0]" />
        </>
      ) : (
        <div className="mt-1 flex flex-col space-y-1">
          <p className="mt-1 font-Hemmers text-black">
            {selectedBuilding.address}
            <p>{`${selectedBuilding.buildingCity || ""}, ${selectedBuilding.buildingState || ""} ${selectedBuilding.buildingZip || ""}`}</p>
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <span className="cursor-pointer font-Hemmers text-sm text-blue-500">
                Edit Customer
              </span>
            </DialogTrigger>
            <DialogContent className="border-transparent bg-inherit">
              <DialogHeader>
                <DialogTitle className="text-black">Edit Customer</DialogTitle>
                <DialogDescription>
                  Fill in to edit a customer
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={handleSubmitEditBuilding(async (data) => {
                  if (selectedBuilding) {
                    try {
                      await updateBuildingFn({
                        buildingId: selectedBuilding.id || "",
                        buildingName: data.buildingName,
                        letterIdBuilding: data.letterIdBuilding,
                        address: data.address,
                        buildingCity: data.buildingCity,
                        buildingState: data.buildingState,
                        buildingZip: data.buildingZip,
                        buildingCountry: data.buildingCountry,
                      });
                      setIsEditOpen(false);
                    } catch (err) {
                      console.error(err);
                      toast.error("Error updating building");
                    }
                  }
                })}
              >
                <div className="flex space-x-4">
                  {/* Container for building name input */}
                  <div className="flex w-96 flex-col">
                    <label
                      htmlFor="buildingName"
                      className="block text-sm font-medium text-black"
                    >
                      Building Name
                    </label>
                    <Input
                      {...registerEditBuilding("buildingName", {
                        required: "Building name is required",
                      })}
                      type="text"
                      id="buildingName"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                    {errorsEditBuilding.buildingName && (
                      <span className="text-sm text-rose-500">
                        {errorsEditBuilding.buildingName.message}
                      </span>
                    )}
                  </div>

                  {/* Container for letter ID input */}
                  <div className="flex w-16 flex-col">
                    <label
                      htmlFor="letterIdBuilding"
                      className="block text-sm font-medium text-black"
                    >
                      Letter Id
                    </label>
                    <Input
                      {...registerEditBuilding("letterIdBuilding", {
                        required: "Building letter id is required",
                      })}
                      type="text"
                      id="letterIdBuilding"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                    {errorsEditBuilding.letterIdBuilding && (
                      <span className="text-sm text-rose-500">
                        {errorsEditBuilding.letterIdBuilding.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Container for address input */}
                <div className="mt-4">
                  <label
                    htmlFor="address"
                    className="block text-sm font-medium text-black"
                  >
                    Address
                  </label>
                  <Input
                    {...registerEditBuilding("address", {
                      required: "Building address is required",
                    })}
                    type="text"
                    id="address"
                    autoComplete="off"
                    className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                  />
                  {errorsEditBuilding.address && (
                    <span className="text-sm text-rose-500">
                      {errorsEditBuilding.address.message}
                    </span>
                  )}
                </div>
                <div className="mt-4 flex space-x-4">
                  <div>
                    <label
                      htmlFor="state"
                      className="block text-sm font-medium text-black"
                    >
                      State
                    </label>
                    <Controller
                      name="buildingState"
                      control={controlEditBuilding}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger
                            className={`mt-1 w-[80px] rounded-md border-zinc-300 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm ${
                              field.value ? "text-zinc-900" : "text-gray-500"
                            }`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="w-[80px]">
                            {states.map((state) => (
                              <SelectItem key={state.value} value={state.label}>
                                {state.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-sm font-medium text-black"
                    >
                      City
                    </label>
                    <Input
                      type="text"
                      id="buildingCity"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                      {...registerEditBuilding("buildingCity")}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="zip"
                      className="block text-sm font-medium text-black"
                    >
                      Zip
                    </label>
                    <Input
                      type="text"
                      id="buildingZip"
                      autoComplete="off"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                      {...registerEditBuilding("buildingZip")}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Button
                    disabled={isSubmittingEditBuilding}
                    className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
                    type="submit"
                    onClick={() => {
                      setIsEditOpen(false);
                    }}
                  >
                    Edit Customer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
